import React, { useCallback } from 'react'
import useSWR from 'swr'
import { Badge, Banner, ImageSlot } from '../../../components'
import {
  BannerImageType,
  deleteImage,
  fetchImages,
  uploadImage,
} from '../../../services/api'
import formStyles from '../../../styles/forms.module.scss'
import styles from './BannerImageEditor.module.scss'

interface Slot {
  type: BannerImageType
  label: string
  hint: string
}

const SLOTS: Slot[] = [
  { type: 'banner_image_desktop', label: 'Desktop', hint: 'Default — used when a device-specific image isn’t set' },
  { type: 'banner_image_tablet',  label: 'Tablet',  hint: 'Falls back to desktop' },
  { type: 'banner_image_mobile',  label: 'Mobile',  hint: 'Falls back to desktop' },
  { type: 'banner_image_tv',      label: 'TV',      hint: 'Falls back to desktop' },
]

export interface BannerImageMetadata {
  alt_text?: string
  caption?: string
  link_url?: string
  link_target?: '_self' | '_blank'
}

interface BannerImageEditorProps {
  metadata: BannerImageMetadata
  onChange: (metadata: BannerImageMetadata) => void
  widgetId?: string
}

interface WidgetImageRow {
  tc_image_id: string
  cdn_url: string
  pixel_width: number | null
  pixel_height: number | null
  type_code: string
}

const BannerImageEditor: React.FC<BannerImageEditorProps> = ({ metadata, onChange, widgetId }) => {
  const update = useCallback(
    (patch: Partial<BannerImageMetadata>) => onChange({ ...metadata, ...patch }),
    [metadata, onChange],
  )

  const { data, mutate } = useSWR(
    widgetId ? ['widget-images', widgetId] : null,
    () => fetchImages('widget', widgetId!) as Promise<{ images: WidgetImageRow[] }>,
    { revalidateOnFocus: false },
  )
  // Reshape the flat list into a lookup keyed by slot type_code.
  const imagesByType: Partial<Record<BannerImageType, WidgetImageRow>> = {}
  for (const img of data?.images ?? []) {
    imagesByType[img.type_code as BannerImageType] = img
  }

  const linkTargetValue = metadata.link_target || '_self'
  const linkUrlTrimmed = metadata.link_url?.trim() || ''

  return (
    <>
      <div className={formStyles.fieldGroup}>
        <label>Alt Text <Badge variant="secondary" size="small">Public</Badge></label>
        <input
          type="text"
          value={metadata.alt_text || ''}
          onChange={(e) => update({ alt_text: e.target.value })}
          placeholder="Describe the banner for screen readers"
        />
      </div>

      <div className={formStyles.fieldGroup}>
        <label>Caption <Badge variant="secondary" size="small">Public</Badge></label>
        <input
          type="text"
          value={metadata.caption || ''}
          onChange={(e) => update({ caption: e.target.value })}
          placeholder="Optional text shown below the banner"
        />
      </div>

      <div className={formStyles.fieldGroup}>
        <label>Link URL <Badge variant="secondary" size="small">Public</Badge></label>
        <input
          type="url"
          value={metadata.link_url || ''}
          onChange={(e) => update({ link_url: e.target.value })}
          placeholder="https://... (optional)"
        />
      </div>

      <div className={formStyles.fieldGroup}>
        <label>Link Target</label>
        <div className={styles.radioRow}>
          {([
            { value: '_self', label: 'Same tab' },
            { value: '_blank', label: 'New tab' },
          ] as const).map(opt => (
            <label key={opt.value} className={styles.radioLabel}>
              <input
                type="radio"
                name="link_target"
                value={opt.value}
                checked={linkTargetValue === opt.value}
                onChange={() => update({ link_target: opt.value })}
                disabled={!linkUrlTrimmed}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      <div className={styles.slotsHeader}>Images</div>
      {!widgetId && (
        <div className={styles.saveFirst}>
          <Banner variant="info" message="Name the widget above to unlock image uploads." />
        </div>
      )}
      {widgetId && (
        <div className={styles.slotsGrid}>
          {SLOTS.map(slot => {
            const img = imagesByType[slot.type]
            return (
              <ImageSlot
                key={slot.type}
                label={slot.label}
                hint={slot.hint}
                image={img ? { url: img.cdn_url, width: img.pixel_width, height: img.pixel_height } : null}
                aspectRatio="16 / 9"
                onUpload={async (file) => {
                  await uploadImage('widget', widgetId, file, { image_type: slot.type })
                  await mutate()
                }}
                onRemove={img ? async () => {
                  await deleteImage('widget', widgetId, img.tc_image_id)
                  await mutate()
                } : undefined}
              />
            )
          })}
        </div>
      )}
    </>
  )
}

export default BannerImageEditor
